import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, MessageCircle, Play, MapPin, ArrowLeft, ArrowUpRight } from "lucide-react";
import { API_URL, LOCAL_URL } from "../constants";
import {
  Avatar,
  DownloadAppCard,
  PageError,
  PageLoading,
  PublicShell,
  imageFallback,
} from "../components/PublicPage";
import PageMetadata from "../components/PageMetadata";
import { useOpenInApp } from "../helpers/deep-link-wrapper";

export default function Post() {
  const { id, postPublicId } = useParams();
  const [post, setPost] = useState<any>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const postPath = postPublicId
      ? `post/public/${postPublicId}`
      : `post/${id}`;
    fetch(`${process.env.NODE_ENV === "production" ? API_URL : LOCAL_URL}/website/${postPath}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setPost)
      .catch(() => setFailed(true));
  }, [id, postPublicId]);
  const publicPostId = post?.event?.id && post?.code != null
    ? `${post.event.id.replace(/-/g, "").slice(0, 8)}${post.code}`
    : undefined;
  useOpenInApp(publicPostId ? `p/${publicPostId}` : undefined);

  if (failed)
    return (
      <PageError message="This post may have been removed, or is only available in the app." />
    );
  if (!post) return <PageLoading />;
  const author = post.user
    ? `${post.user.firstName || ""} ${post.user.lastName || ""}`.trim()
    : post.provider?.name || "CityMoshi member";
  const authorUsername = post.user?.username;
  const media = [...(post.images || []), ...(post.videos || [])];
  return <PublicShell className="page-post">
    <PageMetadata title={post.content?.slice(0, 60) || `Post from ${post.event.name}`} description={post.content || post.event.description} image={post.images?.[0] || post.event.image} url={`/p/${publicPostId}`} />
    <main className="detail-main">
      <article className="wrap po__wrap">
        <Link className="po__back" to={`/e/${post.event.slug}`}><ArrowLeft />Back to event</Link>
        <header className="po__head">
          <Avatar src={post.user?.picture || post.provider?.picture} name={author} />
          <div className="po__who"><Link to={authorUsername ? `/${authorUsername}` : "#"}><b>{author}</b></Link><time dateTime={post.createdAt}>{new Date(post.createdAt).toLocaleDateString("en", { month: "long", day: "numeric", year: "numeric" })}</time></div>
        </header>
        {post.content && <h1 className="po__text detail-post-copy">{post.content}</h1>}
        {!post.content && <h1 className="sr-only">A moment shared by {author}</h1>}
        {media.length > 0 && <div className="po__media" data-n={Math.min(media.length, 3)}>
          {media.slice(0, 3).map((src: string) => <div className="po__shot detail-post-shot" key={src}>
            {(post.videos || []).includes(src) ? <><img src={post.video_thumbnails?.[(post.videos || []).indexOf(src)] || imageFallback} alt="Video thumbnail" /><Play className="detail-video-icon" aria-label="Video preview" /></> : <img src={src} alt={`Moment shared by ${author}`} />}
          </div>)}
        </div>}
        <Link className="po__event" to={`/e/${post.event.slug}`}><MapPin /><span>{post.event.name}</span><ArrowUpRight className="go" /></Link>
        <div className="detail-post-metrics"><span><Heart />{post._count?.likes || 0}<span className="sr-only">likes</span></span><span><MessageCircle />{post._count?.comments || 0}<span className="sr-only">comments</span></span></div>
        <section className="po__comments" aria-labelledby="post-comments-title">
          <div className="detail-section-heading"><h2 className="ev-h" id="post-comments-title"><MessageCircle />Comments</h2><span>{post._count?.comments || 0} total</span></div>
          {post.comments?.length ? <div className="po-cs">{post.comments.map((comment: any) => {
            const name = comment.user ? `${comment.user.firstName || ""} ${comment.user.lastName || ""}`.trim() : comment.provider?.name || "CityMoshi member";
            return <article className="po-c" key={comment.id}><Avatar src={comment.user?.picture || comment.provider?.picture} name={name} /><div className="po-c__body"><div className="po-c__head"><b>{name}</b><time dateTime={comment.createdAt}>{new Date(comment.createdAt).toLocaleDateString("en", { month: "short", day: "numeric" })}</time></div><p>{comment.content}</p><div className="po-c__meta"><span><Heart />{comment._count?.likes || 0} likes</span></div></div></article>;
          })}</div> : <p className="po__empty">No comments yet.</p>}
          {post._count?.comments > 5 && <a className="detail-inline-link" href="#download">See all comments in the app <ArrowUpRight /></a>}
          <div className="detail-conversation"><div><b>Be part of the conversation.</b><p>Share your moments and connect with the crowd in the app.</p></div><a className="btn btn--primary btn--sm" href="#download">Get the app <ArrowUpRight /></a></div>
        </section>
      </article>
      <div className="wrap"><DownloadAppCard /></div>
    </main>
  </PublicShell>;
}
